import React, { useContext } from 'react';
import { Menu, Popover, Button } from '@milanote/roma';
import { OverlayTriggerStateContext } from 'react-aria-components';
import { useDispatch } from 'react-redux';

import { Label } from '../../../../../../../common/labels/labelTypes';
import { useTranslator } from '../../../../../../components/i18n/useTranslator';
import { deleteLabel } from '../../../../../../../common/labels/labelActions';
import LabelColorPicker from './LabelColorPicker';
import { closePopup, openPopup } from '../../../../../../components/popupPanel/popupActions';
import { PopupIds } from '../../../../../../components/popupPanel/popupConstants';
import boardSearchPopupStayActivePredicate from '../../../../../header/workspaceToolsHeader/boardSearch/boardSearchPopupStayActivePredicate';
import LabelUsageText from '../labelUsage/LabelUsageText';
import { useLabelUsageData } from '../labelUsage/useLabelUsageData';

type Props = {
    label: Label;
    onRename: () => void;
    onClose: () => void;
};

const LabelDeleteConfirmationPopoverContent: React.FC<{ onDelete: () => void }> = ({ onDelete }) => {
    const translate = useTranslator('popup', 'labels.edit');
    const deletePopoverState = useContext(OverlayTriggerStateContext);

    return (
        <div className="flex flex-col gap-xl">
            <p className="text-center text-md text-secondary">{translate('deleteDescription')}</p>

            <div className="flex gap-3">
                <Button color="secondary" size="md" className="flex-1" onClick={() => deletePopoverState?.close()}>
                    {translate('deleteCancel')}
                </Button>
                <Button color="primary-destructive" size="md" className="flex-1" onClick={onDelete}>
                    {translate('deleteConfirm')}
                </Button>
            </div>
        </div>
    );
};

const LabelListItemActionsPopover: React.FC<Props> = ({ label, onRename, onClose }) => {
    const dispatch = useDispatch();
    const translate = useTranslator('popup', 'labels.edit');
    const usageDataState = useLabelUsageData(label._id);

    const handleOpenLabelSearch = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();

        dispatch(closePopup(PopupIds.LABEL_EDIT_MENU));
        dispatch(
            openPopup({
                popupId: PopupIds.SEARCH,
                data: { initialSearchText: label.name, labelId: label._id },
                predicateFn: boardSearchPopupStayActivePredicate,
            }),
        );
    };

    const handleDelete = () => {
        dispatch(deleteLabel(label._id));
        onClose();
    };

    return (
        <>
            <Popover.Body>
                <Menu aria-label={`${label.name} actions`} size="sm">
                    <Menu.Item id={`rename-${label._id}`} label={translate('rename')} onAction={onRename} />

                    <Menu.Submenu triggerMode="click">
                        <Menu.Item id={`change-color-${label._id}`} label={translate('changeColor')} />
                        <Menu.SubmenuPopover className="flex">
                            <Popover.Body className="flex h-full items-center justify-center p-4 [--popup-padding:--spacing(4)]">
                                <LabelColorPicker label={label} />
                            </Popover.Body>
                        </Menu.SubmenuPopover>
                    </Menu.Submenu>

                    <Menu.Submenu triggerMode="click">
                        <Menu.Item id={`delete-${label._id}`} label={translate('delete')} destructive />
                        <Menu.SubmenuPopover className="w-55">
                            <Popover.Body className="py-2xl px-2xl">
                                <LabelDeleteConfirmationPopoverContent onDelete={handleDelete} />
                            </Popover.Body>
                        </Menu.SubmenuPopover>
                    </Menu.Submenu>
                </Menu>
            </Popover.Body>

            <Menu.Footer>
                <LabelUsageText onUsageLinkClick={handleOpenLabelSearch} usageDataState={usageDataState} />
            </Menu.Footer>
        </>
    );
};

export default LabelListItemActionsPopover;
